import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import SearchPage from './SearchPage.js';
import { GlobalShell } from './GlobalShell.js';
import type { AuthState } from './GlobalShell.js';
import { AdminSection } from './AdminSection.js';
import { RepoList } from './RepoList.js';
import {
  LAST_SEARCH_COUNTS_KEY,
  parseLastSearchCounts,
  readLastSearchCounts,
  toCountRecord,
  type SearchCounts,
} from './searchCounts.js';

// Core Application Server health endpoint. In production the SPA is served from
// CloudFront and calls the Core API via api.repolens.awsprinl1np.click. Override
// with VITE_API_BASE during local dev if you want to point at a different host.
const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  'https://api.repolens.awsprinl1np.click';

// S-S12-ITER3 (DEF-S12-5/6).
//
// /onboarding/echo-user — OIDC-gated. Returns 200 with claims when the
// AWSELBAuthSessionCookie is present and valid; 302 to Cognito otherwise.
// The browser's `fetch` follows redirects opaquely on cross-origin calls,
// so we use `redirect: 'manual'` and treat any non-2xx (including the
// opaqueredirect type) as "not signed in".
//
// Note: the health probe (/health) has been removed from the UI per the
// redesign spec (app-redesign.md Part 3 — "What is scrapped"). The probe
// output provided no navigation value and exposed a raw API endpoint URL.
// API unreachability is surfaced via the repo list error state instead.

export const SIGN_IN_URL = `${API_BASE}/auth/sign-in`;

const ECHO_USER_ENDPOINT = `${API_BASE}/onboarding/echo-user`;

type ProbeOutcome =
  | { kind: 'pending' }
  | { kind: 'ok'; body: string }
  | { kind: 'auth-required' }
  | { kind: 'error'; message: string };

type EchoUserResponse = {
  readonly userId?: string;
  readonly email?: string;
  readonly authScheme?: string;
};

type SessionState =
  | { kind: 'pending' }
  | { kind: 'signed-in'; user: EchoUserResponse }
  | { kind: 'signed-out' };

/**
 * Probe a cookie-gated endpoint to detect a signed-in session. Treats any
 * redirect or non-2xx response as "auth required" (i.e., not signed in).
 * `credentials: 'include'` ensures the AWSELBAuthSessionCookie travels on
 * cross-origin requests from the SPA to api.repolens.awsprinl1np.click.
 */
async function probeAuthenticatedEndpoint(
  url: string,
  signal: AbortSignal,
): Promise<ProbeOutcome> {
  try {
    const response = await fetch(url, {
      signal,
      credentials: 'include',
      // Manual redirect surfaces ALB's 302-to-Cognito as a typed response
      // instead of an opaque follow. We treat the redirect as "not signed in".
      redirect: 'manual',
    });
    if (response.type === 'opaqueredirect' || !response.ok) {
      return { kind: 'auth-required' };
    }
    const body = await response.text();
    return { kind: 'ok', body };
  } catch (err: unknown) {
    if ((err as { name?: string }).name === 'AbortError') {
      return { kind: 'pending' };
    }
    // CORS-blocked redirect throws "Failed to fetch" in some browsers — same
    // user-facing meaning as auth-required.
    return { kind: 'auth-required' };
  }
}

function parseEchoUser(body: string): EchoUserResponse {
  try {
    const parsed = JSON.parse(body) as Record<string, unknown>;
    return {
      userId: typeof parsed.userId === 'string' ? parsed.userId : undefined,
      email: typeof parsed.email === 'string' ? parsed.email : undefined,
      authScheme:
        typeof parsed.authScheme === 'string' ? parsed.authScheme : undefined,
    };
  } catch {
    return {};
  }
}

// S-S12-ITER7 (DEF-S12-10).
//
// Sign-in is rendered as an anchor element styled as a button rather than a
// <button onClick={...}>. Rationale:
//
// 1. Robustness against bundler/runtime surprises. An <a href> moves the
//    navigation target into a plain DOM attribute that any inspector / test /
//    minifier preserves verbatim.
//
// 2. Native progressive-enhancement. The link works without JS, with
//    middle-click (open in new tab), and is keyboard-focusable by default.

// ---- HomePage (formerly App) ----
// S-UI-002: Rebuilt with dense row list, and extracted components.
// Session state is owned by Root and passed in via props. The persistent header
// is in GlobalShell. The old health section, tagline, and card grid are gone.
// AdminSection has moved to AdminPage (/admin route).

interface RepoSummary {
  fullName: string;
  org: string;
  repo: string;
  indexedAt?: string;
  nodeCount?: number;
  // S-ADMINLOOKUP-001 (non-blocking follow-up, ADR-S-ADMINLOOKUP-001 Decision 1):
  // additive field on GET /api/search/repositories; optional for type-safety only.
  defaultBranch?: string;
}

interface HomePageProps {
  session: SessionState;
  authState: AuthState;
}

function HomePage({ session, authState }: HomePageProps) {
  const [indexedRepos, setIndexedRepos] = useState<RepoSummary[]>([]);
  const [repoListState, setRepoListState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [reindexState, setReindexState] = useState<Record<string, 'idle' | 'loading' | 'error'>>({});

  // S-S84-001 / S-DM-007: Last search result counts per repo — lazy
  // localStorage initializer (Hot #111) reading the versioned envelope through
  // the shared helper. An entry written by the pre-migration build carries no
  // version, so it is discarded rather than rendered: its values were page
  // sizes, not true counts.
  const [lastSearchCounts, setLastSearchCounts] = useState<SearchCounts>(() => {
    try {
      return readLastSearchCounts();
    } catch {
      return {};
    }
  });

  // S-S59-001: Starred/pinned repos — lazy localStorage initializer (Hot #111), Set<string> (Hot #112)
  const [starredRepos, setStarredRepos] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('repolens:starredRepos');
      return saved ? new Set<string>(JSON.parse(saved) as string[]) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  const fetchRepos = useCallback(() => {
    setRepoListState('loading');
    fetch(`${API_BASE}/api/search/repositories?q=`, {
      credentials: 'include',
      headers: { 'User-Agent': 'RepoLens-UI/1.0' },
    })
      .then(r => r.json())
      .then(data => {
        setIndexedRepos(
          (
            data as {
              repositories?: RepoSummary[];
            }
          ).repositories ?? [],
        );
        setRepoListState('loaded');
      })
      .catch(() => setRepoListState('error'));
  }, []);

  const handleReindex = (fullName: string, org: string, repo: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setReindexState(prev => ({ ...prev, [fullName]: 'loading' }));
    fetch(`${API_BASE}/repositories/${org}/${repo}/onboard`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'User-Agent': 'RepoLens-UI/1.0', 'Content-Type': 'application/json' },
    })
      .then(resp => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        setReindexState(prev => ({ ...prev, [fullName]: 'idle' }));
        fetchRepos();
      })
      .catch(() => {
        setReindexState(prev => ({ ...prev, [fullName]: 'error' }));
        setTimeout(() => {
          setReindexState(prev => ({ ...prev, [fullName]: 'idle' }));
        }, 3000);
      });
  };

  useEffect(() => {
    if (session.kind !== 'signed-in') return;
    fetchRepos();
  }, [fetchRepos, session.kind]);

  // S-S84-001: Sync lastSearchCounts across tabs (storage event) and on same-tab writes
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === LAST_SEARCH_COUNTS_KEY) {
        // Same versioned parse as the initializer — a cross-tab write from an
        // older build is discarded, not displayed (S-DM-007).
        setLastSearchCounts(parseLastSearchCounts(event.newValue ?? null));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // S-S59-001: Persist starred repos to localStorage (Hot #115 — after all derived consts)
  useEffect(() => {
    try {
      localStorage.setItem('repolens:starredRepos', JSON.stringify([...starredRepos]));
    } catch {}
  }, [starredRepos]);

  // S-S59-001: Toggle star on a repo
  const toggleStar = (repoName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setStarredRepos(prev => {
      const next = new Set(prev);
      next.has(repoName) ? next.delete(repoName) : next.add(repoName);
      return next;
    });
  };

  return (
    <div className="home-page">
      {/* Auth message strip — signed-out state only */}
      {session.kind === 'signed-out' && (
        <p className="home-auth-message">
          You are not signed in. Search requires authentication.{' '}
          <a
            href={SIGN_IN_URL}
            className="signin-button"
            data-testid="home-signin-btn"
            role="button"
          >
            Sign in
          </a>
        </p>
      )}

      {/* Repo list — dense rows */}
      <RepoList
        authState={authState}
        repos={indexedRepos}
        loading={repoListState === 'loading'}
        error={repoListState === 'error'}
        starredRepos={starredRepos}
        lastSearchCounts={toCountRecord(lastSearchCounts)}
        reindexState={reindexState}
        onStar={toggleStar}
        onReindex={handleReindex}
        onRetry={fetchRepos}
      />
    </div>
  );
}

// ---- AdminPage ----
// Renders the repo onboard/re-index form at /admin. AdminSection is always
// expanded here — no toggle step required.

function AdminPage(): ReactNode {
  const handleSuccess = useCallback(() => {
    // Admin page does not maintain its own repo list;
    // success callback is a no-op here.
  }, []);

  return (
    <div className="admin-page" data-testid="admin-page">
      <AdminSection expanded={true} onSuccess={handleSuccess} />
    </div>
  );
}

// ---- Root ----
// Owns session state. Wraps all page content in GlobalShell which renders the
// persistent header. Delegates to HomePage (/), SearchPage (/search), or
// AdminPage (/admin) based on window.location.pathname.

function Root() {
  const [session, setSession] = useState<SessionState>({ kind: 'pending' });

  // Auth probe — runs once on mount, shared across all pages via Root
  useEffect(() => {
    const controller = new AbortController();

    void probeAuthenticatedEndpoint(ECHO_USER_ENDPOINT, controller.signal).then(
      (outcome) => {
        if (outcome.kind === 'ok') {
          setSession({ kind: 'signed-in', user: parseEchoUser(outcome.body) });
        } else if (outcome.kind === 'auth-required' || outcome.kind === 'error') {
          setSession({ kind: 'signed-out' });
        }
      },
    );

    return () => controller.abort();
  }, []);

  const authState: AuthState =
    session.kind === 'pending'
      ? 'loading'
      : session.kind === 'signed-in'
        ? 'signed-in'
        : 'signed-out';

  const userEmail =
    session.kind === 'signed-in'
      ? (session.user.email ?? session.user.userId ?? 'authenticated user')
      : undefined;

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isSearch = pathname === '/search';
  const isAdmin  = pathname === '/admin';

  return (
    <GlobalShell
      authState={authState}
      userEmail={userEmail}
    >
      {isSearch ? (
        <SearchPage sessionKind={session.kind} />
      ) : isAdmin ? (
        <AdminPage />
      ) : (
        <HomePage session={session} authState={authState} />
      )}
    </GlobalShell>
  );
}

export default Root;
